import { http, Result, querystring, Fider } from "@fider/services"
import { Post, Vote, ImageUpload } from "@fider/models"

export const getAllPosts = async (): Promise<Result<Post[]>> => {
  return await http.get<Post[]>("/api/v1/posts")
}

export interface SearchPostsParams {
  query?: string
  view?: string
  sort?: string
  limit?: number
  tags?: string[]
}

export const searchPosts = async (params: SearchPostsParams): Promise<Result<Post[]>> => {
  return await http.get<Post[]>(
    `/api/v1/posts${querystring.stringify({
      tags: params.tags,
      query: params.query,
      sort: params.sort,
      view: params.view,
      limit: params.limit,
    })}`
  )
}

export const deletePost = async (postNumber: number, text: string): Promise<Result> => {
  return http
    .delete(`/api/v1/posts/${postNumber}`, {
      text,
    })
    .then(http.event("post", "delete"))
}

export const addVote = async (postNumber: number): Promise<Result> => {
  return http.post(`/api/v1/posts/${postNumber}/votes`).then(http.event("post", "vote"))
}

export const removeVote = async (postNumber: number): Promise<Result> => {
  return http.delete(`/api/v1/posts/${postNumber}/votes`).then(http.event("post", "unvote"))
}

export const subscribe = async (postNumber: number): Promise<Result> => {
  return http.post(`/api/v1/posts/${postNumber}/subscription`).then(http.event("post", "subscribe"))
}

export const unsubscribe = async (postNumber: number): Promise<Result> => {
  return http.delete(`/api/v1/posts/${postNumber}/subscription`).then(http.event("post", "unsubscribe"))
}

export const listVotes = async (postNumber: number): Promise<Result<Vote[]>> => {
  return http.get<Vote[]>(`/api/v1/posts/${postNumber}/votes`)
}

export const createComment = async (postNumber: number, content: string, attachments: ImageUpload[]): Promise<Result> => {
  return http.post(`/api/v1/posts/${postNumber}/comments`, { content, attachments }).then(http.event("comment", "create"))
}

export const updateComment = async (postNumber: number, commentID: number, content: string, attachments: ImageUpload[]): Promise<Result> => {
  return http.put(`/api/v1/posts/${postNumber}/comments/${commentID}`, { content, attachments }).then(http.event("comment", "update"))
}

export const deleteComment = async (postNumber: number, commentID: number): Promise<Result> => {
  return http.delete(`/api/v1/posts/${postNumber}/comments/${commentID}`).then(http.event("comment", "delete"))
}

interface SetResponseInput {
  status: string
  text: string
  originalNumber: number
  assignedUserId: string
  userId: number
  approved: boolean
}

export const respond = async (postNumber: number, input: SetResponseInput): Promise<Result> => {
  return http
    .put(`/api/v1/posts/${postNumber}/status`, {
      status: input.status,
      text: input.text,
      originalNumber: input.originalNumber,
      assignedUserId: input.assignedUserId,
      userId: input.userId,
      approved: input.approved
    })
    .then(http.event("post", "respond"))
}

interface SetBountyInput {
  bounty: string
  description: string
  userId: number
}

export const updatePostBounty = async (postNumber: number, input: SetBountyInput): Promise<Result> => {
  const data = parseFloat(input.bounty.replace('$',''))
  return http.put(`/api/v1/post-bounty/${postNumber}`, { bounty: data, bountyDescription: input.description, userId: input.userId }).then(http.event("post", "update"))
}

interface CreatePostResponse {
  id: number
  number: number
  title: string
  slug: string
}

export const createPost = async (title: string, description: string, attachments: ImageUpload[]): Promise<Result<CreatePostResponse>> => {
  return http.post<CreatePostResponse>(`/api/v1/posts`, { title, description, attachments }).then(http.event("post", "create"))
}

export const updatePost = async (postNumber: number, title: string, description: string, attachments: ImageUpload[]): Promise<Result> => {
  return http.put(`/api/v1/posts/${postNumber}`, { title, description, attachments }).then(http.event("post", "update"))
}

export const archivePost = async (postNumber: number): Promise<Result> => {
  return http
    .put(`/api/v1/posts/${postNumber}/status`, {
      status: "archived",
      text: "Post archived",
      originalNumber: 0,
      assignedUserId: "",
      userId: Fider.session.user.id,
      approved: false
    })
    .then(http.event("post", "archive"))
}

export const restorePost = async (postNumber: number): Promise<Result> => {
  return http
    .put(`/api/v1/posts/${postNumber}/status`, {
      status: "open",
      text: "Post restored from archive",
      originalNumber: 0,
      assignedUserId: "",
      userId: Fider.session.user.id,
      approved: false
    })
    .then(http.event("post", "restore"))
}
